import { Encoding } from '@mode-vegas/vegas';
import { ChartTypes } from '@mode/shared/contract-common';
import { EncodingHelpers } from '..';
import { SchemaModel } from './schema-model';
import { Area100Translator } from './translators/area-100-translator';
import { AreaTranslator } from './translators/area-translator';
import { Bar100Translator } from './translators/bar-100-translator';
import { BarLineTranslator } from './translators/bar-line-translator';
import { BarTranslator } from './translators/bar-translator';
import { GroupedBarTranslator } from './translators/grouped-bar-translator';
import { PieTranslator } from './translators/pie-translator';

export function translateHighChartsToMuze(
  chartType: ChartTypes.ChartType,
  encoding: Encoding,
  format: Readonly<ChartTypes.ChartFormat>,
  model: SchemaModel
): Encoding {
  const translator = makeTranslator(chartType, model);
  const muzeEncoding = translator.translateHighChartsToMuze(encoding, format);
  return EncodingHelpers.setMissingAnalyticFrames(muzeEncoding, model);
}

export function translateMuzeToHighCharts(
  chartType: ChartTypes.ChartType,
  encoding: Encoding,
  format: Readonly<ChartTypes.ChartFormat>,
  model: SchemaModel
): Encoding {
  const translator = makeTranslator(chartType, model);
  const hcEncoding = translator.translateMuzeToHighCharts(encoding, format);
  return clearAnalyticFrames(hcEncoding, model);
}

export function formatRequiresEncodingUpdate(
  chartType: ChartTypes.ChartType,
  oldFormat: Readonly<ChartTypes.ChartFormat>,
  newFormat: Readonly<ChartTypes.ChartFormat>,
  model: SchemaModel
): boolean {
  const translator = makeTranslator(chartType, model);
  return translator.formatRequiresEncodingUpdate(oldFormat, newFormat);
}

export interface ChartTranslator {
  translateHighChartsToMuze(encoding: Encoding, format: Readonly<ChartTypes.ChartFormat>): Encoding;

  translateMuzeToHighCharts(encoding: Encoding, format: Readonly<ChartTypes.ChartFormat>): Encoding;

  formatRequiresEncodingUpdate(
    oldFormat: Readonly<ChartTypes.ChartFormat>,
    newFormat: Readonly<ChartTypes.ChartFormat>
  ): boolean;
}

function makeTranslator(chartType: ChartTypes.ChartType, model: SchemaModel) {
  switch (chartType) {
    case 'bar':
    case 'hBar':
      return new GroupedBarTranslator(chartType === 'hBar');
    case 'stackedBar':
    case 'hStackedBar':
      return new BarTranslator();
    case 'stackedBar100':
    case 'hStackedBar100':
      return new Bar100Translator();
    case 'linePlusBar':
      return new BarLineTranslator();
    case 'pie':
    case 'donut':
      return new PieTranslator(model);
    case 'totalArea':
      return new Area100Translator();
    case 'area':
      return new AreaTranslator();
    case 'line':
    case 'scatter':
    default:
      return new NoopTranslator();
  }
}

/**
 * Clears the frame for any analytic calc field in the encoding.
 */
function clearAnalyticFrames(encoding: Encoding, model: SchemaModel) {
  const encFields = EncodingHelpers.collectEncFields(encoding);
  let result = encoding;
  encFields.forEach((field) => {
    const compiled = model.compileEncField(field);
    if (compiled.isAnalyticCalc && field.id != null) {
      result = EncodingHelpers.clearFrame(result, field.id);
    }
  });
  return result;
}

class NoopTranslator implements ChartTranslator {
  translateHighChartsToMuze(encoding: Encoding, format: Readonly<ChartTypes.ChartFormat>): Encoding {
    return encoding;
  }

  translateMuzeToHighCharts(encoding: Encoding, format: Readonly<ChartTypes.ChartFormat>): Encoding {
    return encoding;
  }

  formatRequiresEncodingUpdate(
    oldFormat: Readonly<ChartTypes.ChartFormat>,
    newFormat: Readonly<ChartTypes.ChartFormat>
  ): boolean {
    return false;
  }
}
